import { type CustomerId } from '@solinkcorp/api-types';
import settings from '@solinkcorp/settings';
import { assertNever, getRawQueryParameters } from '@solinkcorp/utils';
import Browsers from '@solinkcorp/utils/browsers';
import { addEmphasis } from '@solinkcorp/utils/LoggingUtil';
import { auth } from '@solinkcorp/vd-common';
import { trackUserPreferenceChange } from 'core/analytics';
import Cookie from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import type React from 'react';
import { S3 } from 'utils/S3';
import * as auth0 from '../auth0';
import { LOGOUT_REASON } from '../authConstants';
import { isCorrectTenantId, logout } from '../authUtils';
import { authorize, impersonateUser, updateUserPreferences } from '../callHome/callHomeAuth';
import {
  type CallHomeAuthorizeResult,
  type CallHomeCookie,
  type JWTUser,
  UserIdentityType,
  type UserPreferences,
} from '../callHome/callHomeAuthTypes';
import { type GuestUserSession, type ImpersonatorSession, type UserSession } from '../types';

// Local storage keys
const GUEST_AUTH_TOKEN_KEY = 'GUEST_AUTH_TOKEN';
const IMPERSONATOR_ID_KEY = `IMPERSONATOR_ID`;

export interface SetInitialStateAction {
  type: 'setInitialState';
  userSession: UserSession | undefined;
  impersonatorSession: ImpersonatorSession | undefined;
  /**
   * loggingOut is set true if there's an existing session but it can't be
   * used for some reason (e.g. it's expired).  We have to wait for auth0 to
   * logout before we do anything.
   */
  loggingOut: boolean;
}

export interface LoginAction {
  type: 'login';
  userSession: UserSession | ImpersonatorSession;
}

export interface RefreshAction {
  type: 'refresh';
  userSession: UserSession | ImpersonatorSession;
}

export interface ImpersonateAction {
  type: 'impersonate';
  userSession: UserSession;
}

/**
 * Refresh an impersonated session.  Re-sets both the impersonator session and
 * the impersonated user's session at once.
 */
export interface RefreshImpersonatedAction {
  type: 'refreshImpersonated';
  impersonator: ImpersonatorSession;
  userSession: UserSession;
}

export interface UpdatePreferencesAction {
  type: 'setPreferences';
  preferences: UserPreferences;
}

export type AuthAction =
  | SetInitialStateAction
  | LoginAction
  | RefreshAction
  | ImpersonateAction
  | RefreshImpersonatedAction
  | UpdatePreferencesAction;

/**
 * Parse and validate the provided JWT token.
 */
function parseJWTToken(jwt: string): JWTUser {
  const jwtUser = jwtDecode(jwt) as JWTUser;
  if (jwtUser.exp * 1000 < Date.now()) {
    throw new Error('Token is expired');
  }
  return jwtUser;
}

/**
 * Retrieve the customer ID of a user, or throw if the user has none.
 */
function getCustomerId(jwtUser: JWTUser) {
  const customerId =
    jwtUser.app_metadata.customerId || jwtUser.app_metadata.tenantId || jwtUser.tenantId;
  if (!customerId) {
    throw new Error('Token is missing customerId');
  }
  return customerId as CustomerId;
}

/**
 * Convert a CallHomeAuthorizeResult into a CallHomeUserSession, and set any
 * cookies needed for this session.
 */
async function startSession(
  callHome: CallHomeAuthorizeResult,
): Promise<UserSession | ImpersonatorSession> {
  const isGuest = callHome.profile.identityType === UserIdentityType.GUEST_USER;
  const jwtUser = parseJWTToken(callHome.accessToken);

  const s3 = callHome.aws ? new S3(callHome.aws) : undefined;

  if (auth0.isImpersonator(callHome.profile)) {
    return {
      ...callHome,
      type: 'impersonator',
      userId: callHome.profile.id,
      jwtUser,
    };
  }

  const thirdPartyCookiesSet = await setCookies({
    jwtToken: callHome.accessToken,
    cookies: callHome.cookies,
    usePreflight: callHome.profile.features?.usePreflight,
  });

  return {
    ...callHome,
    type: isGuest ? 'guest' : 'callHome',
    jwtUser,
    thirdPartyCookiesSet,
    customerId: getCustomerId(jwtUser),
    userId: callHome.profile.id,
    isSolinkUser: auth.isSolinkEmail(jwtUser.email),
    isDemoUser: /demo@customer.com/.test(jwtUser.email),
    s3,
  };
}

/**
 * Load a guest session from the query parameters or from local storage.
 */
async function loadGuestSession(
  queryParams: URLSearchParams,
): Promise<GuestUserSession | undefined> {
  let guestSession: GuestUserSession | undefined;

  // Guest URL will be of the format https://solinkcloud.com/#/?tenantId=temporary&accessToken=...
  const guestAccessToken =
    queryParams.get('accessToken') || localStorage.getItem(GUEST_AUTH_TOKEN_KEY);
  if (guestAccessToken) {
    try {
      const jwtUser = parseJWTToken(guestAccessToken);
      if (jwtUser.app_metadata.identityType !== 'GUEST_USER') {
        throw new Error(
          `User has identity type ${jwtUser.app_metadata.identityType}, expected GUEST_USER.`,
        );
      }

      if (jwtUser.exp * 1000 < Date.now()) {
        throw new Error(`Guest access token is expired`);
      }

      const authResult = await authorize(guestAccessToken);
      const session = await startSession(authResult);
      if (session.type !== 'guest') {
        throw new Error('Expected guest session');
      }

      guestSession = session;

      localStorage.setItem(GUEST_AUTH_TOKEN_KEY, guestAccessToken);
    } catch (err) {
      console.error('Invalid guest JWT token', err);
    }
  }

  return guestSession;
}

/**
 * Set all required cookies for the given user.
 *
 * This will set a cookie for solinkcloud.com to talk to cloud-VMS, and will
 * also try to set "third party" cookies for .solink.direct to talk to devices
 * (either over the relay or directly).  Depending on the browser and the user's
 * settings, the third party cookie may fail.
 *
 * Returns true if third party cookies were set, false otherwise.
 */
async function setCookies(parameters: {
  jwtToken: string;
  cookies?: CallHomeCookie[];
  usePreflight?: boolean;
}): Promise<boolean> {
  const { jwtToken, cookies } = parameters;

  // Cloud front cookies from call-home.
  if (cookies) {
    cookies.forEach((cookie) => {
      // TODO Investigate: Gerry suggested vms.solinkcloud.com domain scope for cookies
      Cookie.set(cookie.name, cookie.value, { domain: settings.domain_solink });
    });
  }

  const browser = new Browsers();

  let forcePreflight = false;

  if (parameters.usePreflight) {
    console.warn(addEmphasis(`Forcing preflight because of user preference.`));
    forcePreflight = true;
  } else if (browser.isSafari) {
    // Safari does not support setting cross domain cookies
    console.warn(
      `Safari browser detected, falling back to preflight requests for local VMS communication.`,
    );
    forcePreflight = true;
  }

  // TODO: Need to unset these cookies on logout.  We need to add an `auth.unsetCookies()`.
  const { localCookieSet } = await auth.setCookies(jwtToken, {
    setLocalCookie: !forcePreflight,
    cloudVmsDomain: settings.domain_solink,
    solinkAuthUrl: settings.auth_solink,
  });

  if (!localCookieSet && !forcePreflight) {
    console.warn(
      addEmphasis(
        `Could not set third party cookie, falling back to preflight requests for local VMS communication.`,
      ),
    );
  }

  return localCookieSet;
}

/**
 * Generates an action to populate the initial state of the reducer at startup.
 */
export async function loadInitialState(): Promise<SetInitialStateAction> {
  let loggingOut = false;
  let userSession: UserSession | undefined;
  let impersonatorSession: ImpersonatorSession | undefined;

  // Check with auth0 to see if a user is already logged in...
  try {
    const callHomeAuthResult = await auth0.checkSession();
    const session = callHomeAuthResult && (await startSession(callHomeAuthResult));

    if (session) {
      switch (session.type) {
        case 'callHome':
          userSession = session;
          break;
        case 'impersonator':
          impersonatorSession = session;
          try {
            const impersonationId = localStorage.getItem(IMPERSONATOR_ID_KEY);
            if (impersonationId) {
              const impersonated = await impersonateUser(
                impersonatorSession.accessToken,
                impersonationId,
              );
              const impersonatedSession = await startSession(impersonated);
              if (impersonatedSession.type === 'impersonator') {
                throw new Error('Cannot impersonate an impersonator!');
              }
              userSession = impersonatedSession;
            }
          } catch (err) {
            console.error('Could not impersonate user', err);
          }
          break;
        case 'guest':
          throw new Error('Cannot load guest sessions from auth0');
        default:
          assertNever(session);
      }
    }
  } catch (err) {
    if ((err as any).code === 'login_required') {
      // No user logged in, continue
    } else {
      console.error('Error checking existing session', err);
    }
  }

  const query = getRawQueryParameters(window.location);

  // Check to see if a guest user is trying to login...
  const guestAccessTokenPresent = !!query.get('accessToken');
  if (guestAccessTokenPresent && userSession) {
    // Logout whoever is already logged in.
    auth0.logout(
      userSession,
      [],
      LOGOUT_REASON.AUTH0_USER_ALREADY_LOGGED_IN,
      window.location.href,
      { source: 'loadInitialState#', message: 'Logging out previous user to start guest session' },
    );
    loggingOut = true;
  } else if (!userSession) {
    // Try to load a guest session before we do anything else...
    userSession = await loadGuestSession(query);
  }

  // If there's already a user logged in, but the current URL is for a specific
  // customer/tenant, then make sure we're logged in on the correct customer.
  // Note that for guest access, then tenantId will always be "temporary".
  if (!isCorrectTenantId(window.location, userSession?.customerId)) {
    if (impersonatorSession) {
      // Clear the impersonated user
      localStorage.removeItem(IMPERSONATOR_ID_KEY);
    } else {
      // Redirect to the logout page so we'll show a nice message.
      logout('WRONG_CUSTOMER', window.location.href);
      loggingOut = true;
    }

    userSession = undefined;
  }

  return {
    type: 'setInitialState',
    userSession,
    impersonatorSession,
    loggingOut,
  };
}

export async function loginAction(
  callHomeAuthResult: CallHomeAuthorizeResult,
): Promise<LoginAction> {
  const userSession = await startSession(callHomeAuthResult);

  return {
    type: 'login',
    userSession,
  };
}

/**
 * Impersonate a user, or refresh an impersonation session.
 * Reason is provided during initial impersonation
 */
export async function impersonateAction(
  impersonatorAuthToken: string,
  userIdToImpersonate: string,
  reason?: string,
  bypassCache?: boolean,
): Promise<ImpersonateAction> {
  const authResult = await impersonateUser(
    impersonatorAuthToken,
    userIdToImpersonate,
    reason,
    bypassCache,
  );
  const userSession = await startSession(authResult);
  if (userSession.type === 'impersonator') {
    throw new Error('Cannot impersonate an impersonator');
  }

  // Store who we are impersonating so if we reload the page, we'll remember.
  localStorage.setItem(IMPERSONATOR_ID_KEY, userIdToImpersonate);

  return {
    type: 'impersonate',
    userSession,
  };
}

let inFlightRefresh: Promise<RefreshAction | RefreshImpersonatedAction> | undefined;

/**
 * Refresh the currently logged in auth0 user.
 *
 * If the user is impersonating someone, this refreshes the impersonator session
 * and re-impersonates the target user with the fresh impersonator token from
 * `checkSession`, so both the impersonator session and the impersonated user's
 * session and cookies are re-set.  This mirrors `loadInitialState`.
 *
 * Concurrent callers (the refresh timers and the tab-refocus check) share one
 * in-flight refresh.  `auth0.checkSession` uses a single transaction slot, so
 * overlapping calls would clobber each other and one would fail.  Sharing the
 * promise runs `checkSession` once and gives every caller the same result.
 */
export function refreshSessionAction(): Promise<RefreshAction | RefreshImpersonatedAction> {
  if (!inFlightRefresh) {
    inFlightRefresh = doRefreshSession().finally(() => {
      inFlightRefresh = undefined;
    });
  }
  return inFlightRefresh;
}

async function doRefreshSession(): Promise<RefreshAction | RefreshImpersonatedAction> {
  const callHomeAuthResult = await auth0.checkSession(true);

  if (!callHomeAuthResult) {
    throw new Error('User is not logged in');
  }

  const session = await startSession(callHomeAuthResult);

  if (session.type === 'impersonator') {
    const impersonationId = localStorage.getItem(IMPERSONATOR_ID_KEY);
    if (impersonationId) {
      // Re-impersonate the target user with the fresh impersonator token, so
      // the impersonated session and its cookies are re-set.  Keep the fresh
      // impersonator session too, so `state.impersonator` does not go stale.
      const impersonated = await impersonateAction(
        session.accessToken,
        impersonationId,
        undefined,
        true,
      );
      return {
        type: 'refreshImpersonated',
        impersonator: session,
        userSession: impersonated.userSession,
      };
    }
  }

  return {
    type: 'refresh',
    userSession: session,
  };
}

export async function updatePreferences(
  dispatch: React.Dispatch<AuthAction>,
  accessToken: string,
  currentPreferences: UserPreferences | undefined,
  newPreferences: Partial<UserPreferences>,
): Promise<void> {
  // Optimistically update
  if (currentPreferences) {
    dispatch({ type: 'setPreferences', preferences: { ...currentPreferences, ...newPreferences } });
  }

  try {
    const updatedPreferences = await updateUserPreferences(accessToken, newPreferences);
    trackUserPreferenceChange(newPreferences);
    dispatch({ type: 'setPreferences', preferences: updatedPreferences });
  } catch (_error) {
    // If we fail, undo the optimistic update.
    if (currentPreferences) {
      dispatch({ type: 'setPreferences', preferences: currentPreferences });
    }
  }
}
